/**
 * Video Blocks Shared JavaScript - Component-Based Refactor
 * 视频组件共享脚本文件 - 基于组件的重构
 */

// -----------------------------------------------------------------------------
// YouTube API Loader (Singleton Pattern)
// -----------------------------------------------------------------------------
if (typeof window.YouTubeAPI === "undefined") {
  window.YouTubeAPI = (() => {
    let promise;

    function load() {
      if (promise) {
        return promise;
      }

      promise = new Promise((resolve) => {
        if (window.YT && window.YT.Player) {
          resolve(window.YT);
          return;
        }

        const script = document.createElement("script");
        script.src = "https://www.youtube.com/iframe_api";

        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag.parentNode.insertBefore(script, firstScriptTag);

        window.onYouTubeIframeAPIReady = () => {
          resolve(window.YT);
        };
      });

      return promise;
    }

    return {
      load: load,
    };
  })();
}

// -----------------------------------------------------------------------------
// 1. VideoBlocksItem Component (<video-blocks-item>)
// -----------------------------------------------------------------------------
var VideoBlocksItem = class extends HTMLElement {
  constructor() {
    super();
    this.videoPlayer = this.querySelector(".video-player");
    this.loadingSpinner = this.videoPlayer?.querySelector(".video-player__loading");

    if (!this.videoPlayer) {
      console.error("❌ 初始化失败: <video-blocks-item> 内部未找到 .video-player 元素", this);
      return;
    }

    this.playButton = this.videoPlayer.querySelector("[data-play-button]");
    this.videoEl = this.videoPlayer.querySelector("video");
    this.iframeEl = this.videoPlayer.querySelector("iframe");
    this.videoType = this.videoPlayer.dataset.videoType;
    this.muteButton = this.videoPlayer.querySelector("[data-mute-button]");
    this.fullscreenButton = this.videoPlayer.querySelector("[data-fullscreen-button]");
    this.controls = this.videoPlayer.querySelector(".video-player__controls");
    this.closeBtn = this.querySelector(".close_btn"); // 使用现有的关闭按钮
    this.player = null; // 用于存储 YouTube API 播放器实例
    this.onKeyUp = this.onKeyUp.bind(this);
    this.isFullscreen = false; // 全屏状态标志
    this.loadingTimeout = null; // 加载超时定时器
  }

  connectedCallback() {
    if (!this.videoPlayer) return;

    // iOS 设备检测
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // 在 iOS 上，为 YouTube 启用原生控制栏（包括全屏按钮）
    if (isIOS && this.videoType === "youtube" && this.iframeEl) {
      const currentSrc = this.iframeEl.src;
      if (currentSrc.includes("controls=0")) {
        // 启用控制栏和全屏按钮
        let newSrc = currentSrc.replace("controls=0", "controls=1");
        // 确保全屏按钮启用
        if (!newSrc.includes("fs=1")) {
          newSrc = newSrc.replace("fs=0", "fs=1");
        }
        this.iframeEl.src = newSrc;
      }
      // 在 iOS 上隐藏我们的自定义控制按钮
      if (this.controls) {
        this.controls.style.display = "none";
      }
      if (this.fullscreenButton) {
        this.fullscreenButton.style.display = "none";
      }
    }

    // TikTok 视频特殊处理
    if (this.videoType === "tiktok" && this.iframeEl) {
      // 尝试监听 TikTok iframe 的消息
      this.setupTikTokMessageListener();

      // iOS 上隐藏所有自定义控制按钮（使用 TikTok 自带的）
      if (isIOS) {
        if (this.fullscreenButton) {
          this.fullscreenButton.style.display = "none";
        }
        if (this.controls) {
          this.controls.style.display = "none";
        }
      }
    }

    this.setupEventListeners();
    this.updateMuteButton();

    if (this.videoType === "youtube") {
      this.initializeYouTubePlayer();
    } else if (this.videoType === "vimeo") {
      this.setupVimeoLoadingDetection();
    }
  }

  setupEventListeners() {
    this.playButton?.addEventListener("click", this.handlePlayButtonClick.bind(this));
    this.videoPlayer.addEventListener("click", this.handlePlayerClick.bind(this));
    this.muteButton?.addEventListener("click", this.toggleMute.bind(this));
    this.fullscreenButton?.addEventListener("click", this.handleFullscreenClick.bind(this));

    // 给关闭按钮添加点击事件监听
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.exitFullscreen();
      });
    }

    // 添加键盘事件监听（在整个页面级别，因为全屏时可能需要全局监听）
    document.addEventListener("keyup", this.onKeyUp);

    if (this.videoEl) {
      this.setupNativeVideoEvents();
    }
  }

  handlePlayButtonClick(e) {
    e.stopPropagation();

    // 针对 Instagram 的特殊处理，实现“穿透点击”
    if (this.videoType === "instagram") {
      const overlay = this.videoPlayer.querySelector("[data-overlay]");
      if (overlay) {
        overlay.style.pointerEvents = "none";
      }
    }

    this.playVideo();
  }

  handlePlayerClick(e) {
    if (e.target.closest(".video-player__controls") || e.target.closest("[data-play-button]")) {
      return;
    }
    if (this.videoPlayer.dataset.state === "playing") {
      this.pauseVideo();
    } else {
      this.playVideo();
    }
  }

  handleFullscreenClick(e) {
    e?.stopPropagation();

    const isFullscreen =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (isFullscreen) {
      this.exitFullscreen();
    } else {
      this.enterFullscreen();
    }
  }

  enterFullscreen() {
    // iOS Safari 特殊处理
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (isIOS) {
      // iOS 本地视频：使用 webkitEnterFullscreen()
      if (this.videoEl && typeof this.videoEl.webkitEnterFullscreen === "function") {
        this.videoEl.controls = true;
        try {
          this.videoEl.webkitEnterFullscreen();
          this.addIOSFullscreenListeners();
        } catch (error) {
          console.error("iOS 全屏失败:", error);
        }
        return;
      }

      // iOS YouTube/iframe 视频：尝试 iframe 全屏
      if (this.iframeEl && this.videoType === "youtube") {
        try {
          if (this.iframeEl.requestFullscreen) {
            this.iframeEl.requestFullscreen();
          } else if (this.iframeEl.webkitRequestFullscreen) {
            this.iframeEl.webkitRequestFullscreen();
          } else if (this.iframeEl.webkitEnterFullscreen) {
            this.iframeEl.webkitEnterFullscreen();
          } else {
            console.warn(`📱 iOS: ${this.videoType} iframe 不支持全屏，提示用户使用原生全屏按钮`);
            // 可以显示提示信息
          }
          this.addFullscreenEventListeners();
        } catch (error) {
          console.error(`iOS ${this.videoType} iframe 全屏失败:`, error);
        }
        return;
      }
    }

    // 其他浏览器：使用容器全屏，这样自定义关闭按钮可以作为"控件"显示在 video 上方
    // video 通过 controlsList="nofullscreen" 禁用了原生全屏按钮
    const elementToFullscreen = this.querySelector(".video-player__container");

    if (elementToFullscreen.requestFullscreen) {
      elementToFullscreen.requestFullscreen();
    } else if (elementToFullscreen.webkitRequestFullscreen) {
      /* Safari Desktop */
      elementToFullscreen.webkitRequestFullscreen();
    } else if (elementToFullscreen.mozRequestFullScreen) {
      /* Firefox */
      elementToFullscreen.mozRequestFullScreen();
    } else if (elementToFullscreen.msRequestFullscreen) {
      /* IE11 */
      elementToFullscreen.msRequestFullscreen();
    }

    // 监听全屏状态变化
    this.addFullscreenEventListeners();
  }

  exitFullscreen() {
    // iOS Safari 特殊处理
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (isIOS && this.videoEl && typeof this.videoEl.webkitExitFullscreen === "function") {
      try {
        this.videoEl.webkitExitFullscreen();
      } catch (error) {
        console.error("iOS 退出全屏失败:", error);
      }
      return;
    }

    // 其他浏览器
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      /* Safari Desktop */
      document.webkitExitFullscreen();
    } else if (document.mozCancelFullScreen) {
      /* Firefox */
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      /* IE11 */
      document.msExitFullscreen();
    }

    // 先撤回全屏再执行UI更新
    this.onExitFullscreen();
  }

  addFullscreenEventListeners() {
    const handleFullscreenChange = () => {
      const isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );

      if (isFullscreen) {
        // 检查是否是我们自己的元素在全屏
        const fullscreenElement =
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement;

        if (fullscreenElement && this.contains(fullscreenElement)) {
          // 进入全屏
          this.isFullscreen = true;
          this.onEnterFullscreen();
        }
      } else {
        // 退出全屏
        this.isFullscreen = false;
        this.onExitFullscreen();
      }

      // 移除监听器，避免重复监听
      this.removeFullscreenEventListeners();
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    // 保存引用以便后续移除
    this.fullscreenChangeHandler = handleFullscreenChange;
  }

  removeFullscreenEventListeners() {
    if (this.fullscreenChangeHandler) {
      document.removeEventListener("fullscreenchange", this.fullscreenChangeHandler);
      document.removeEventListener("webkitfullscreenchange", this.fullscreenChangeHandler);
      document.removeEventListener("mozfullscreenchange", this.fullscreenChangeHandler);
      document.removeEventListener("MSFullscreenChange", this.fullscreenChangeHandler);
      this.fullscreenChangeHandler = null;
    }
  }

  addIOSFullscreenListeners() {
    if (!this.videoEl) return;

    const handleIOSBeginFullscreen = () => {
      this.isFullscreen = true;
      // iOS 全屏时 video 自带 controls，不需要额外处理
    };

    const handleIOSEndFullscreen = () => {
      this.isFullscreen = false;
      // 退出全屏后禁用 controls
      if (this.videoEl) {
        this.videoEl.controls = false;
      }
    };

    this.iosBeginFullscreenHandler = handleIOSBeginFullscreen;
    this.iosEndFullscreenHandler = handleIOSEndFullscreen;

    this.videoEl.addEventListener("webkitbeginfullscreen", handleIOSBeginFullscreen);
    this.videoEl.addEventListener("webkitendfullscreen", handleIOSEndFullscreen);
  }

  removeIOSFullscreenListeners() {
    if (this.videoEl && this.iosBeginFullscreenHandler && this.iosEndFullscreenHandler) {
      this.videoEl.removeEventListener("webkitbeginfullscreen", this.iosBeginFullscreenHandler);
      this.videoEl.removeEventListener("webkitendfullscreen", this.iosEndFullscreenHandler);
      this.iosBeginFullscreenHandler = null;
      this.iosEndFullscreenHandler = null;
    }
  }

  setupTikTokMessageListener() {
    if (!this.iframeEl || this.videoType !== "tiktok") return;

    const handleTikTokMessage = (event) => {
      // 验证消息来源（TikTok）
      if (!event.origin.includes("tiktok.com")) return;

      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        // 尝试解析 TikTok 的消息格式
        if (data.type) {
          switch (data.type) {
            case "play":
            case "playing":
              this.videoPlayer.dataset.state = "playing";
              this.toggleOverlay(false);
              this.togglePoster(false);
              break;
            case "pause":
            case "paused":
              this.videoPlayer.dataset.state = "paused";
              this.toggleOverlay(true);
              break;
            case "ended":
              this.videoPlayer.dataset.state = "ended";
              this.toggleOverlay(true);
              this.togglePoster(true);
              break;
          }
        }
      } catch (error) {
        // 忽略解析错误，TikTok 可能发送其他格式的消息
        console.debug("TikTok 消息解析失败:", error);
      }
    };

    this.tiktokMessageHandler = handleTikTokMessage;
    window.addEventListener("message", handleTikTokMessage);
  }

  removeTikTokMessageListener() {
    if (this.tiktokMessageHandler) {
      window.removeEventListener("message", this.tiktokMessageHandler);
      this.tiktokMessageHandler = null;
    }
  }

  onEnterFullscreen() {
    // 隐藏控制栏
    if (this.controls) {
      this.controls.style.display = "none";
    }

    // 对于 YouTube 视频，启用原生控制栏（但禁用全屏按钮，因为我们有自己的关闭按钮）
    if (this.videoType === "youtube" && this.iframeEl) {
      const currentSrc = this.iframeEl.src;
      if (currentSrc.includes("controls=0")) {
        // 启用控制栏，但禁用全屏按钮 (fs=0)
        let newSrc = currentSrc.replace("controls=0", "controls=1");
        if (newSrc.includes("fs=1")) {
          newSrc = newSrc.replace("fs=1", "fs=0");
        }
        this.iframeEl.src = newSrc;
      }
    }

    // 对于本地视频，启用原生控制栏
    if (this.videoEl && (this.videoType === "local" || this.videoType === "video_url")) {
      this.videoEl.controls = true;
    }

    // 显示关闭按钮（容器全屏时会自动通过CSS显示）
    if (this.closeBtn) {
      this.closeBtn.style.display = "flex";
    }
  }

  onExitFullscreen() {
    // 显示控制栏
    if (this.controls) {
      this.controls.style.display = "flex";
    }

    // 对于 YouTube 视频，禁用原生控制栏并恢复全屏按钮设置
    if (this.videoType === "youtube" && this.iframeEl) {
      const currentSrc = this.iframeEl.src;
      if (currentSrc.includes("controls=1")) {
        // 禁用控制栏，恢复全屏按钮设置
        let newSrc = currentSrc.replace("controls=1", "controls=0");
        if (newSrc.includes("fs=0")) {
          newSrc = newSrc.replace("fs=0", "fs=1");
        }
        this.iframeEl.src = newSrc;
      }
    }

    // 对于本地视频，禁用原生控制栏
    if (this.videoEl && (this.videoType === "local" || this.videoType === "video_url")) {
      this.videoEl.controls = false;
    }

    // 隐藏关闭按钮
    if (this.closeBtn) {
      this.closeBtn.style.display = "none";
    }
  }

  onKeyUp(e) {
    if (e.key === "Escape" && this.isFullscreen) {
      this.handleFullscreenClick(e);
    }
  }

  disconnectedCallback() {
    // 清理全屏相关资源
    this.removeFullscreenEventListeners();
    this.removeIOSFullscreenListeners();

    // 清理 TikTok 消息监听
    this.removeTikTokMessageListener();

    // 清理键盘事件监听
    document.removeEventListener("keyup", this.onKeyUp);

    // 清理加载超时定时器
    this.clearLoadingTimeout();
  }

  // --- YouTube Player API Integration ---
  initializeYouTubePlayer() {
    if (!this.iframeEl) return;

    // 确保 iframe 有一个唯一的 ID
    if (!this.iframeEl.id) {
      this.iframeEl.id = `youtube-player-${Math.random().toString(36).substr(2, 9)}`;
    }

    // 加载 API 并创建播放器
    window.YouTubeAPI.load().then((YT) => {
      this.player = new YT.Player(this.iframeEl.id, {
        events: {
          onReady: this.onPlayerReady.bind(this),
          onStateChange: this.onPlayerStateChange.bind(this),
        },
      });
    });
  }

  onPlayerReady(event) {
    this.updateMuteButton(); // API 准备好后再次更新静音状态
  }

  onPlayerStateChange(event) {
    switch (event.data) {
      case YT.PlayerState.BUFFERING:
        this.videoPlayer.dataset.state = "loading";
        this.showLoadingSpinner();
        this.setLoadingTimeout();
        break;
      case YT.PlayerState.PLAYING:
        this.videoPlayer.dataset.state = "playing";
        this.hideLoadingSpinner();
        this.clearLoadingTimeout();
        this.toggleOverlay(false);
        this.togglePoster(false);
        break;
      case YT.PlayerState.PAUSED:
        this.videoPlayer.dataset.state = "paused";
        this.hideLoadingSpinner();
        this.clearLoadingTimeout();
        this.toggleOverlay(true);
        break;
      case YT.PlayerState.ENDED:
        this.videoPlayer.dataset.state = "ended";
        this.hideLoadingSpinner();
        this.clearLoadingTimeout();
        this.toggleOverlay(true);
        this.togglePoster(true);
        // 重置 Instagram 的 pointer-events，以便在结束后可以再次点击播放
        if (this.videoType === "instagram") {
          const overlay = this.videoPlayer.querySelector("[data-overlay]");
          if (overlay) {
            overlay.style.pointerEvents = "auto";
          }
        }
        break;
      case YT.PlayerState.UNSTARTED:
        this.videoPlayer.dataset.state = "paused";
        this.hideLoadingSpinner();
        this.clearLoadingTimeout();
        this.toggleOverlay(true);
        break;
    }
  }

  async playVideo() {
    // 设置加载状态
    this.videoPlayer.dataset.state = "loading";
    this.showLoadingSpinner();

    // 设置加载超时保护机制
    this.setLoadingTimeout();

    try {
      if (this.videoEl) {
        await this.videoEl.play();
        // 本地视频播放成功后会通过事件监听器更新状态
      } else if (this.player && typeof this.player.playVideo === "function" && this.player.getPlayerState() !== -1) {
        this.player.playVideo();
        // YouTube视频播放状态会通过API事件更新
      } else if (this.iframeEl) {
        // Fallback for Vimeo or if YouTube API hasn't loaded/ready yet
        let currentSrc = this.iframeEl.src;
        if (!currentSrc.includes("autoplay=1")) {
          if (this.videoType === "youtube") {
            this.iframeEl.src = currentSrc + "&autoplay=1&mute=1";
          } else if (this.videoType === "vimeo") {
            this.iframeEl.src = currentSrc + "&autoplay=1&muted=1";
          }
        }
        // 非YouTube视频在成功播放后需要手动更新状态
        if (this.videoType !== "youtube") {
          // 给iframe一点时间加载
          setTimeout(() => {
            if (this.videoPlayer.dataset.state === "loading") {
              this.videoPlayer.dataset.state = "playing";
              this.hideLoadingSpinner();
              this.toggleOverlay(false);
              this.togglePoster(false);
            }
          }, 2000);
        }
      }
    } catch (error) {
      console.error("❌ 播放视频失败", error, this);
      this.videoPlayer.dataset.state = "error";
      this.hideLoadingSpinner();
      this.clearLoadingTimeout();
    }
  }

  pauseVideo() {
    try {
      if (this.videoEl) {
        this.videoEl.pause();
      } else if (this.player && typeof this.player.pauseVideo === "function") {
        this.player.pauseVideo();
      } else if (this.iframeEl) {
        // Fallback for Vimeo or if YouTube API hasn't loaded yet
        let currentSrc = this.iframeEl.src;
        if (this.videoType === "vimeo") {
          this.iframeEl.src = currentSrc.replace(/&autoplay=1/, "").replace(/&muted=1/, "");
        }
      }
      // State updates are now handled by API state change events for YouTube
      if (this.videoType !== "youtube") {
        this.videoPlayer.dataset.state = "paused";
        this.toggleOverlay(true);
      }
    } catch (error) {
      console.error("❌ 暂停视频失败", error, this);
    }
  }

  toggleMute(e) {
    e.stopPropagation();
    if (this.videoEl) {
      this.videoEl.muted = !this.videoEl.muted;
    } else if (this.player && typeof this.player.isMuted === "function") {
      if (this.player.isMuted()) {
        this.player.unMute();
      } else {
        this.player.mute();
      }
      // 手动触发更新，因为 YouTube API 的状态更新可能不会触发 volumechange
      setTimeout(() => this.updateMuteButton(), 100);
    }
  }

  updateMuteButton() {
    if (!this.muteButton) return;

    let isMuted = true;
    if (this.videoEl) {
      isMuted = this.videoEl.muted;
    } else if (this.player && typeof this.player.isMuted === "function") {
      isMuted = this.player.isMuted();
    }

    const muteIcon = this.muteButton.querySelector("[data-mute-icon]");
    const unmuteIcon = this.muteButton.querySelector("[data-unmute-icon]");

    if (muteIcon && unmuteIcon) {
      muteIcon.style.display = isMuted ? "none" : "block";
      unmuteIcon.style.display = isMuted ? "block" : "none";
    }
  }

  setupNativeVideoEvents() {
    this.videoEl.addEventListener("loadstart", () => {
      if (this.videoPlayer.dataset.state !== "error") {
        this.videoPlayer.dataset.state = "loading";
        this.showLoadingSpinner();
        this.setLoadingTimeout();
      }
    });

    this.videoEl.addEventListener("loadeddata", () => {
      this.hideLoadingSpinner();
      this.clearLoadingTimeout();
    });

    this.videoEl.addEventListener("play", () => {
      this.videoPlayer.dataset.state = "playing";
      this.hideLoadingSpinner();
      this.clearLoadingTimeout();
      this.toggleOverlay(false);
      this.togglePoster(false);
    });

    this.videoEl.addEventListener("pause", () => {
      this.videoPlayer.dataset.state = "paused";
      this.toggleOverlay(true);
    });

    this.videoEl.addEventListener("ended", () => {
      this.videoPlayer.dataset.state = "ended";
      this.toggleOverlay(true);
      this.togglePoster(true);
      this.videoEl.currentTime = 0;
      this.hideLoadingSpinner();
      this.clearLoadingTimeout();
      // 重置 Instagram 的 pointer-events，以便在结束后可以再次点击播放
      if (this.videoType === "instagram") {
        const overlay = this.videoPlayer.querySelector("[data-overlay]");
        if (overlay) {
          overlay.style.pointerEvents = "auto";
        }
      }
    });

    this.videoEl.addEventListener("error", () => {
      console.error("❌ 本地视频加载出错");
      this.videoPlayer.dataset.state = "error";
      this.hideLoadingSpinner();
      this.clearLoadingTimeout();
    });

    this.videoEl.addEventListener("volumechange", this.updateMuteButton.bind(this));
  }

  toggleOverlay(show) {
    const overlay = this.videoPlayer.querySelector("[data-overlay]");
    if (overlay) {
      overlay.style.opacity = show ? "1" : "0";
      overlay.style.pointerEvents = show ? "auto" : "none";
    }
  }

  togglePoster(show) {
    const poster = this.videoPlayer.querySelector(".video-player__poster");
    if (poster) {
      poster.style.display = show ? "block" : "none";
    }
  }

  showLoadingSpinner() {
    if (this.loadingSpinner) {
      this.loadingSpinner.style.display = "block";
    }
  }

  hideLoadingSpinner() {
    if (this.loadingSpinner) {
      this.loadingSpinner.style.display = "none";
    }
  }

  setupVimeoLoadingDetection() {
    if (!this.iframeEl) return;

    // 监听iframe的load事件来检测Vimeo视频加载完成
    this.iframeEl.addEventListener("load", () => {
      // Vimeo视频加载完成后隐藏加载动画
      setTimeout(() => {
        this.hideLoadingSpinner();
        this.clearLoadingTimeout();
      }, 1000);
    });
  }

  setLoadingTimeout() {
    // 清除之前的超时定时器
    this.clearLoadingTimeout();

    // 设置加载超时保护：如果8秒后还在加载状态，则强制隐藏加载动画
    this.loadingTimeout = setTimeout(() => {
      if (this.videoPlayer.dataset.state === "loading") {
        console.warn("⏰ 视频加载超时，强制隐藏加载动画");
        this.hideLoadingSpinner();
        this.videoPlayer.dataset.state = "paused";
        this.toggleOverlay(true);
      }
    }, 8000);
  }

  clearLoadingTimeout() {
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
      this.loadingTimeout = null;
    }
  }

  preload() {
    if (this.videoEl) {
      this.videoEl.preload = "metadata";
    }
  }
};

if (!window.customElements.get("video-blocks-item")) {
  window.customElements.define("video-blocks-item", VideoBlocksItem);
}

// -----------------------------------------------------------------------------
// 2. VideoBlocksContainer Component (<video-blocks-container>)
// -----------------------------------------------------------------------------
var VideoBlocksContainer = class extends HTMLElement {
  constructor() {
    super();
    this.intersectionObserver = null;
  }

  connectedCallback() {
    this.initVideoPreloader();
  }

  initVideoPreloader() {
    if (!("IntersectionObserver" in window)) return;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const item = entry.target;
            if (item.matches("video-blocks-item") && typeof item.preload === "function") {
              item.preload();
            }
            this.intersectionObserver.unobserve(item);
          }
        });
      },
      { rootMargin: "50px", threshold: 0.1 }
    );

    this.querySelectorAll("video-blocks-item").forEach((item) => {
      this.intersectionObserver.observe(item);
    });
  }

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }
};

if (!window.customElements.get("video-blocks-container")) {
  window.customElements.define("video-blocks-container", VideoBlocksContainer);
}
